import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Calendar, ArrowRight, Camera, Newspaper, Megaphone, CalendarDays, Play, MapPin, Clock } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const tabs = ['All', 'News', 'Press Releases', 'Photo Gallery', 'Events'];
const newsArticles = [{
  type: 'News',
  title: 'Hon. Tumwebaze Promises a Hands-on Approach in the Agriculture Sector',
  excerpt: 'Hon. Frank Tumwebaze, the new Minister of Agriculture, Animal Industry and Fisheries (MAAIF), has promised to apply a proactive approach through directly engaging farmers on ministry policies.',
  date: 'June 22, 2023',
  image: 'https://cdn.magicpatterns.com/uploads/pMxPQ7KwkMHL94QX8ycxEG/fdhjkl_(4).jpg',
  tag: 'Leadership',
  tagColor: 'bg-uganda-red'
}, {
  type: 'News',
  title: 'Ministry Prioritizes Strategic Communication and Modern Online Platforms',
  excerpt: 'MAAIF is investing in digital transformation to improve public access to agricultural information, services, and data through modern web platforms and social media engagement.',
  date: 'May 15, 2023',
  image: 'https://images.unsplash.com/photo-1595508064774-5ff825a66368?w=600&h=400&fit=crop',
  tag: 'Digital',
  tagColor: 'bg-blue-600'
}, {
  type: 'News',
  title: 'Launch of the National Agricultural Sector Portal for the Republic of Uganda',
  excerpt: 'The Ministry has officially launched a comprehensive online portal to provide citizens, farmers, and stakeholders with centralized access to agricultural services and information.',
  date: 'April 28, 2023',
  image: 'https://images.unsplash.com/photo-1605000797499-95a51c5269ae?w=600&h=400&fit=crop',
  tag: 'Launch',
  tagColor: 'bg-uganda-green'
}, {
  type: 'News',
  title: 'Uganda Coffee Exports Hit Record $849 Million in 2023',
  excerpt: "Uganda's coffee exports reached an all-time high of $849 million, driven by increased production, improved quality, and favorable global prices for Robusta coffee.",
  date: 'January 12, 2024',
  image: 'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=600&h=400&fit=crop',
  tag: 'Trade',
  tagColor: 'bg-amber-700'
}];
const pressReleases = [{
  type: 'Press Releases',
  title: 'MAAIF Statement on the Control of Fall Armyworm in Eastern Uganda',
  excerpt: 'The Ministry has deployed rapid response teams and distributed pesticides to affected districts following reports of Fall Armyworm outbreaks in maize-growing areas of Eastern Uganda.',
  date: 'March 5, 2024',
  tag: 'Pest Control',
  tagColor: 'bg-red-600'
}, {
  type: 'Press Releases',
  title: 'Press Statement on Foot-and-Mouth Disease Vaccination Campaign',
  excerpt: 'MAAIF announces a nationwide vaccination campaign targeting 5 million cattle in 42 districts to control the spread of Foot-and-Mouth Disease ahead of the dry season.',
  date: 'February 18, 2024',
  tag: 'Animal Health',
  tagColor: 'bg-purple-600'
}, {
  type: 'Press Releases',
  title: 'Ministry Response to Flooding in Kasese and Bundibugyo Districts',
  excerpt: 'MAAIF is coordinating emergency agricultural relief including seed distribution and livestock feed for farmers affected by severe flooding in the Rwenzori region.',
  date: 'December 10, 2023',
  tag: 'Emergency',
  tagColor: 'bg-orange-600'
}];
const photoGallery = [{
  type: 'Photo Gallery',
  title: "Minister's Field Visit to Karamoja Sub-region",
  count: '24 Photos',
  date: 'March 2024',
  image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=400&fit=crop'
}, {
  type: 'Photo Gallery',
  title: "National Farmers' Day Celebrations 2023",
  count: '36 Photos',
  date: 'October 2023',
  image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=600&h=400&fit=crop'
}, {
  type: 'Photo Gallery',
  title: 'ACDP Project Launch in Mbale District',
  count: '18 Photos',
  date: 'August 2023',
  image: 'https://images.unsplash.com/photo-1592982537447-6f2a6a0c7c18?w=600&h=400&fit=crop'
}, {
  type: 'Photo Gallery',
  title: 'Harvest Money Expo 2023',
  count: '42 Photos',
  date: 'July 2023',
  image: 'https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?w=600&h=400&fit=crop'
}];
const events = [{
  type: 'Events',
  title: 'National Agricultural Show 2024',
  date: 'July 5-7, 2024',
  location: 'Jinja Agricultural Showground',
  description: 'Annual agricultural exhibition showcasing innovations, technologies, and best practices in crop, livestock, and fisheries production.',
  status: 'Upcoming'
}, {
  type: 'Events',
  title: 'World Food Day 2024',
  date: 'October 16, 2024',
  location: 'Kampala Serena Hotel',
  description: 'National celebrations marking World Food Day with the theme "Right to Foods for a Better Life and a Better Future."',
  status: 'Upcoming'
}, {
  type: 'Events',
  title: 'Harvest Money Expo 2024',
  date: 'August 2-4, 2024',
  location: 'UMA Showgrounds, Lugogo',
  description: "East Africa's largest agricultural trade fair bringing together farmers, agribusinesses, financial institutions, and technology providers.",
  status: 'Upcoming'
}];
type ContentItem = {
  type: string;
  title: string;
  excerpt?: string;
  date: string;
  image?: string;
  tag?: string;
  tagColor?: string;
  count?: string;
  location?: string;
  description?: string;
  status?: string;
};
export function MediaPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('All');
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);
  const allContent: ContentItem[] = [...newsArticles, ...pressReleases, ...photoGallery, ...events];
  const filteredContent = activeTab === 'All' ? allContent : allContent.filter(item => item.type === activeTab);
  return <>
      <Header data-id="element-1076" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1077">
        {/* Hero */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1078">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=1600&h=600&fit=crop")'
        }} data-id="element-1079"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1080"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1081">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1082">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1083">
                <Home className="w-4 h-4 mr-1" data-id="element-1084" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1085" />
              <span className="text-white font-medium" data-id="element-1086">Media Center</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1087">
              Media Center
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1088">
              Stay informed with the latest news, press releases, photo
              galleries, and upcoming events from the Ministry of Agriculture,
              Animal Industry and Fisheries.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1089">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 border-b border-gray-200 pb-4" data-id="element-1090">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2.5 rounded-lg font-semibold transition-colors text-sm ${activeTab === tab ? 'bg-uganda-green text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} data-id="element-1091">
                {tab}
              </button>)}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1092">
            {filteredContent.map((item, index) => <motion.div key={`${item.type}-${index}`} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            delay: index * 0.05
          }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group cursor-pointer" data-id="element-1093">
                {/* News & Photo Gallery cards have images */}
                {item.image && <div className="relative h-48 overflow-hidden" data-id="element-1094">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1095" />
                    {item.tag && <span className={`absolute top-3 left-3 ${item.tagColor} text-white text-xs font-bold px-3 py-1 rounded-full`} data-id="element-1096">
                        {item.tag}
                      </span>}
                    {item.type === 'Photo Gallery' && <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1097">
                        <div className="bg-white/90 rounded-full p-3" data-id="element-1098">
                          <Camera className="w-6 h-6 text-uganda-black" data-id="element-1099" />
                        </div>
                      </div>}
                    {item.count && <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1" data-id="element-1100">
                        <Camera className="w-3 h-3" data-id="element-1101" /> {item.count}
                      </span>}
                  </div>}

                <div className="p-5" data-id="element-1102">
                  {/* Type badge for non-image items */}
                  {!item.image && <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-3 ${item.type === 'Press Releases' ? 'bg-amber-100 text-amber-800' : item.type === 'Events' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-1103">
                      {item.type === 'Press Releases' && <Megaphone className="w-3 h-3" data-id="element-1104" />}
                      {item.type === 'Events' && <CalendarDays className="w-3 h-3" data-id="element-1105" />}
                      {item.type}
                    </span>}

                  {/* Event status */}
                  {item.status && <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded-full mb-3 ml-2" data-id="element-1106">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" data-id="element-1107" />
                      {item.status}
                    </span>}

                  <h3 className="font-bold text-lg text-uganda-black mb-2 leading-snug group-hover:text-uganda-green transition-colors" data-id="element-1108">
                    {item.title}
                  </h3>

                  {(item.excerpt || item.description) && <p className="text-sm text-gray-500 mb-4 line-clamp-3" data-id="element-1109">
                      {item.excerpt || item.description}
                    </p>}

                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400" data-id="element-1110">
                    <span className="flex items-center gap-1" data-id="element-1111">
                      <Calendar className="w-3 h-3" data-id="element-1112" /> {item.date}
                    </span>
                    {item.location && <span className="flex items-center gap-1" data-id="element-1113">
                        <MapPin className="w-3 h-3" data-id="element-1114" /> {item.location}
                      </span>}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100" data-id="element-1115">
                    <span className="text-sm font-semibold text-uganda-green flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-1116">
                      {item.type === 'Photo Gallery' ? 'View Gallery' : item.type === 'Events' ? 'Event Details' : 'Read More'}
                      <ArrowRight className="w-4 h-4" data-id="element-1117" />
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </main>
      <Footer data-id="element-1118" />
    </>;
}